import { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import { getAccessToken, useAuthStore } from './stores/useAuthStore';
import { AuthService } from './services/AuthService';
import { ToastContainer, PageSkeleton } from './components/ui';

// Lazy components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const TenantsList = lazy(() => import('./pages/tenants/TenantsList'));
const TenantDetails = lazy(() => import('./pages/tenants/TenantDetails'));
const TenantSettings = lazy(() => import('./pages/tenants/TenantSettings'));
const TenantUsers = lazy(() => import('./pages/tenants/TenantUsers'));
const CustomerList = lazy(() => import('./pages/customers/CustomerList'));
const CustomerDetailsPage = lazy(() => import('./pages/customers/CustomerDetailsPage'));
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const CreateInvoicePage = lazy(() => import('./pages/invoices/CreateInvoicePage'));
const EditInvoicePage = lazy(() => import('./pages/invoices/EditInvoicePage'));
const InvoiceDetailsPage = lazy(() => import('./pages/invoices/InvoiceDetailsPage'));
const PaymentList = lazy(() => import('./pages/payments/PaymentList'));
const GlobalComms = lazy(() => import('./pages/system/GlobalComms'));
const FinancialPerformance = lazy(() => import('./pages/system/FinancialPerformance'));
const PerformanceAnalysis = lazy(() => import('./pages/system/PerformanceAnalysis'));
const CashFlowProjection = lazy(() => import('./pages/system/CashFlowProjection'));
const ExpenseList = lazy(() => import('./pages/expenses/ExpenseList'));
const SubscriptionList = lazy(() => import('./pages/subscriptions/SubscriptionList'));
const CreateExpensePage = lazy(() => import('./pages/expenses/CreateExpensePage'));
const EditExpensePage = lazy(() => import('./pages/expenses/EditExpensePage'));
const ExpenseDetailsPage = lazy(() => import('./pages/expenses/ExpenseDetailsPage'));
const TaxDashboardPage = lazy(() => import('./pages/tax/TaxDashboardPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const HelpPage = lazy(() => import('./pages/help/HelpPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

// A simple wrapper to use Layout with Outlet
const AppLayout = () => (
  <Layout>
    <Outlet />
  </Layout>
);

function App() {
  const { isInitialized, user, logout, setUser, setInitialized } = useAuthStore();
  const isAuthenticated = !!user;
  const bootstrapLock = useRef(false);

  useEffect(() => {
    // Only bootstrap ONCE on mount
    if (bootstrapLock.current) return;
    bootstrapLock.current = true;

    const bootstrap = async () => {
      const storedToken = getAccessToken();

      if (storedToken) {
        try {
          const response = await AuthService.getCurrentUser();

          if (response.success && response.data) {
            setUser(response.data.user);
          } else {
            logout();
          }
        } catch {
          logout();
        }
      } else {
        setUser(null);
      }

      setInitialized(true);
    };

    bootstrap();
  }, [setUser, logout, setInitialized]);

  // Initialization Screen (Prevents layout thrashing)
  if (!isInitialized) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-gray-400 text-sm font-medium">Bootstrapping InvoiceFlow...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            <Route
              path="/login"
              element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              {/* Entry Point Redirects */}
              <Route
                path="/"
                element={
                  user?.role === 'superadmin' ? (
                    <Navigate to="/tenants" replace />
                  ) : (
                    <Navigate to={`/tenants/${user?.tenantId}`} replace />
                  )
                }
              />

              {user?.role === 'superadmin' && (
                <>
                  <Route path="/tenants" element={<TenantsList />} />
                  <Route path="/global-comms" element={<GlobalComms />} />
                </>
              )}

              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/help" element={<HelpPage />} />

              {/* Unified Tenant Scope (Canonical REST) */}
              <Route path="/tenants/:tenantId">
                <Route index element={<Dashboard />} />
                <Route path="details" element={<TenantDetails />} />
                <Route path="settings" element={<TenantSettings />} />
                <Route path="users" element={<TenantUsers />} />
                <Route path="customers" element={<CustomerList />} />
                <Route path="customers/:customerId" element={<CustomerDetailsPage />} />
                <Route path="invoices" element={<InvoiceList />} />
                <Route path="invoices/new" element={<CreateInvoicePage />} />
                <Route path="invoices/:id/edit" element={<EditInvoicePage />} />
                <Route path="invoices/:id/details" element={<InvoiceDetailsPage />} />
                <Route path="payments" element={<PaymentList />} />
                <Route path="expenses" element={<ExpenseList />} />
                <Route path="subscriptions" element={<SubscriptionList />} />
                <Route path="expenses/new" element={<CreateExpensePage />} />
                <Route path="expenses/:id/edit" element={<EditExpensePage />} />
                <Route path="expenses/:id/details" element={<ExpenseDetailsPage />} />
                <Route path="performance" element={<FinancialPerformance />} />
                <Route path="analysis" element={<PerformanceAnalysis />} />
                <Route path="projection" element={<CashFlowProjection />} />
                <Route path="tax" element={<TaxDashboardPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
